import { FileAddOutlined, ToolOutlined } from '@ant-design/icons';
import {
  InventoryUploadDrawer,
  InventoryUploadDrawerHandle,
  ServiceDetailsCard,
  ServiceSelect,
} from '@components';
import { useInventoryCategories } from '@hooks';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Badge, Button, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useRef } from 'react';
import { IOrderServiceSelectProps } from './OrderServiceSelect.props';

const { Text } = Typography;

const sectionStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #f0f7ff 0%, #f8fbff 100%)',
  borderRadius: 10,
  border: '1px solid #d4e4f7',
  padding: '12px 8px 8px',
  marginBottom: 8,
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 10,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: '#1a3353',
};

const iconWrapperStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: 5,
  background: '#2CBDFF',
  color: '#fff',
  fontSize: 12,
};

const OrderServiceSelectMobile: React.FC<IOrderServiceSelectProps> = ({
  atLeastOneService,
  disabled,
  variant = 'selected',
  basket,
}) => {
  const { serviceStore, authenticationStore, orderStore } = useStore();
  const isTechnician = authenticationStore.currentUser?.role === 'Technician';
  const currentOrderId = orderStore.order?.id;
  const { options: categoryOptions } = useInventoryCategories();
  const uploadDrawerRef = useRef<InventoryUploadDrawerHandle>(null);
  const isAdditional = variant === 'additional';
  const isRevision = variant === 'revision';
  // A basket has no additional/revision sets - there is only ever the one job in it.
  const isBasket = !!basket;

  const services = basket
    ? basket.selectedServices
    : isAdditional
      ? serviceStore.additionalSelectedServices
      : isRevision
        ? serviceStore.revisionSelectedServices
        : serviceStore.selectedServices;

  const pricingSummary = basket
    ? basket.pricingSummary
    : isRevision
      ? serviceStore.revisionPricingSummary
      : serviceStore.pricingSummary;

  const handleUploadSuccess = (createdItems: any[], parsed?: any[]) => {
    if (basket) {
      basket.addPartsFromInventoryBulk(createdItems, parsed);
      return;
    }
    serviceStore.addPartsFromInventoryBulk(
      createdItems,
      parsed,
      isAdditional ? 'additional' : isRevision ? 'revision' : 'selected'
    );
  };

  // For the order being edited, keep its end time anchored to the total service duration.
  // A basket has no saved order behind it, so there is nothing to anchor.
  const syncOrderEndTime = () => {
    if (isBasket || variant !== 'selected') return;
    const totalHours = serviceStore.selectedServices.reduce((acc, s) => acc + (s.duration || 0), 0);
    orderStore.syncEndFromServiceDuration(totalHours);
  };

  const handleSelect = (id: number) => {
    if (basket) basket.addService(id);
    else if (isAdditional) serviceStore.addAdditionalService(id);
    else if (isRevision) serviceStore.addRevisionService(id);
    else serviceStore.addService(id);
    syncOrderEndTime();
  };

  const handleDeselect = (id: number) => {
    if (basket) basket.removeService(id, atLeastOneService);
    else if (isAdditional) serviceStore.removeAdditionalService(id);
    else if (isRevision) serviceStore.removeRevisionService(id, atLeastOneService);
    else serviceStore.removeService(id, atLeastOneService);
    syncOrderEndTime();
  };

  const handleClear = () => {
    if (basket) basket.clearSelectedServices();
    else if (isAdditional) serviceStore.clearAdditionalSelectedServices();
    else if (isRevision) serviceStore.clearRevisionSelectedServices();
    else serviceStore.clearSelectedServices();
    syncOrderEndTime();
  };

  return (
    <div style={sectionStyle}>
      <div style={{ ...sectionHeaderStyle, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={iconWrapperStyle}>
            <ToolOutlined />
          </div>
          <Text style={sectionTitleStyle}>Services</Text>
          {services.length > 0 && (
            <Badge
              count={services.length}
              style={{ backgroundColor: '#2CBDFF', fontSize: 10, fontWeight: 600 }}
            />
          )}
        </div>
        {(!isAdditional || isRevision) && services.length > 0 && (
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: '#1a3353',
              background: '#e8f4fd',
              padding: '2px 10px',
              borderRadius: 4,
            }}
          >
            {Utils.formatCurrency(pricingSummary.totalPrice)}
          </span>
        )}
      </div>

      <div style={{ marginBottom: services.length > 0 ? 8 : 0 }}>
        <ServiceSelect
          mode="multiple"
          showMyServicesOnly
          allowClear={!atLeastOneService}
          value={services.map((x) => x.id)}
          defaultValue={services.map((x) => ({
            label: Utils.formatServiceLabel(x),
            value: x.id,
          }))}
          onClear={handleClear}
          onSelect={handleSelect}
          onDeselect={handleDeselect}
          disabled={disabled}
        />
      </div>

      {services.map((selectedService) => (
        <ServiceDetailsCard
          key={selectedService.id}
          service={selectedService}
          disabled={disabled}
          variant={variant}
          basket={basket}
        />
      ))}

      {!disabled && services.length > 0 && !isTechnician && (
        <Button
          block
          icon={<FileAddOutlined />}
          onClick={() =>
            uploadDrawerRef.current?.open({
              orderId: currentOrderId,
              serviceOptions: services.map((s) => ({
                id: s.id,
                name: Utils.formatServiceLabel(s),
              })),
            })
          }
          type="primary"
          style={{ marginTop: 8 }}
        >
          Upload parts
        </Button>
      )}

      <InventoryUploadDrawer
        ref={uploadDrawerRef}
        categoryOptions={categoryOptions}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
};

export default observer(OrderServiceSelectMobile);
